/**
 * Create Fast Campaign Page
 * Step 1: Select Asset + Enter Name
 * Step 2: Enter Context + Generate
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Zap, 
  ArrowLeft,
  ArrowRight,
  Globe,
  Loader2,
  CheckCircle2,
  Sparkles
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CreateFastCampaignPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(true);
  
  // Form state
  const [selectedAssetId, setSelectedAssetId] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [contextText, setContextText] = useState('');
  const [campaignId, setCampaignId] = useState(null);

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    try {
      const response = await axios.get(`${API}/api/assets`);
      const readyAssets = (response.data || []).filter(a => a.status === 'ready');
      setAssets(readyAssets);
    } catch (error) {
      console.error('Error fetching assets:', error);
      toast.error('Failed to load assets');
    } finally {
      setAssetsLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!selectedAssetId) {
      toast.error('Please select an asset');
      return;
    }
    if (!campaignName.trim()) {
      toast.error('Please enter a campaign name');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/fast-campaigns`, {
        name: campaignName,
        asset_id: selectedAssetId
      });
      
      setCampaignId(response.data.id);
      setStep(2);
      toast.success('Fast campaign created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create fast campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!contextText.trim()) {
      toast.error('Please enter campaign context');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/fast-campaigns/${campaignId}/generate`, {
        context_text: contextText
      });
      
      toast.success('Fast campaign generated!');
      navigate(`/fast-campaigns/${campaignId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate fast campaign');
    } finally {
      setLoading(false);
    }
  };

  const selectedAsset = assets.find(a => a.id === selectedAssetId);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/fast-campaigns')}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              <h1 className="text-xl font-semibold text-foreground">
                Create Fast Campaign
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-amber-500' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step >= 1 ? 'bg-amber-500 text-white' : 'bg-muted text-muted-foreground'
            }`}>
              {step > 1 ? <CheckCircle2 className="w-5 h-5" /> : '1'}
            </div>
            <span className="font-medium">Select Asset</span>
          </div>
          <div className="flex-1 h-px bg-border" />
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-amber-500' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step >= 2 ? 'bg-amber-500 text-white' : 'bg-muted text-muted-foreground'
            }`}>
              2
            </div>
            <span className="font-medium">Generate</span>
          </div>
        </div>

        {/* Step 1: Select Asset */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="bg-surface border border-border rounded-xl p-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-foreground">Campaign Name</Label>
                  <Input
                    id="name"
                    placeholder="e.g., SF Tech Landing Page"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    className="mt-2"
                    data-testid="fast-campaign-name-input"
                  />
                </div>

                <div>
                  <Label className="text-foreground">Select Asset</Label>
                  <p className="text-sm text-muted-foreground mt-1 mb-3">
                    Choose an existing asset to personalize
                  </p>
                  
                  {assetsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : assets.length === 0 ? (
                    <div className="text-center py-8 border border-dashed border-border rounded-lg">
                      <Globe className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">
                        No ready assets found. <br />
                        <button 
                          onClick={() => navigate('/assets')}
                          className="text-primary hover:underline"
                        >
                          Create an asset first
                        </button>
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-2 max-h-64 overflow-y-auto">
                      {assets.map((asset) => (
                        <button
                          key={asset.id}
                          onClick={() => setSelectedAssetId(asset.id)}
                          className={`flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                            selectedAssetId === asset.id 
                              ? 'border-amber-500 bg-amber-500/10' 
                              : 'border-border hover:border-muted-foreground/50'
                          }`}
                          data-testid={`asset-option-${asset.id}`}
                        >
                          <Globe className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground truncate">{asset.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{asset.url}</p>
                          </div>
                          {selectedAssetId === asset.id && (
                            <CheckCircle2 className="w-5 h-5 text-amber-500 flex-shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleCreateCampaign}
                disabled={loading || !selectedAssetId || !campaignName.trim()}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="continue-to-context-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4 mr-2" />
                )}
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Enter Context + Generate */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="bg-surface border border-border rounded-xl p-6">
              {selectedAsset && (
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
                  <Globe className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-foreground">{selectedAsset.name}</p>
                    <p className="text-xs text-muted-foreground">{selectedAsset.url}</p>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="context" className="text-foreground">Campaign Context</Label>
                <p className="text-sm text-muted-foreground mt-1 mb-3">
                  Describe who you're targeting and what makes this campaign unique.
                  The AI will conservatively rewrite your hero section to match this context.
                </p>
                <Textarea
                  id="context"
                  placeholder="e.g., Running Google Ads targeting startup founders in San Francisco who need accounting help. They value efficiency and growth."
                  value={contextText}
                  onChange={(e) => setContextText(e.target.value)}
                  className="min-h-32"
                  data-testid="fast-campaign-context-input"
                />
              </div>

              <div className="mt-4 p-3 bg-amber-500/10 rounded-lg">
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  <strong>What will be changed:</strong> Hero headline, paragraph, and optionally the next content section. 
                  Changes are conservative - no new claims or features will be invented.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={loading || !contextText.trim()}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="generate-fast-campaign-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Campaign
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
